import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Share2, Play, Pause, Clock, Star, MessageSquare, Download } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../config/api';
import toast from 'react-hot-toast';
import UpgradeModal from '../components/UpgradeModal';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const StoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [voices, setVoices] = useState([]);
  const utteranceRef = useRef(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    displayName: '',
    rating: 5,
    headline: '',
    content: '',
  });

  const fetchStory = useCallback(async () => {
    try {
      setLoading(true);
      // ID is already encoded in URL, send as-is to backend
      const response = await api.get(`/stories/${id}`);
      if (response.data.success) {
        setStory(response.data.story);
        // Mark as read (fire and forget, don't wait for it)
        // Use encoded ID - backend will decode it automatically
        if (!response.data.story.is_read) {
          api.put(`/stories/${id}`, { isRead: true }).catch(err => {
            console.error('Failed to mark story as read:', err);
          });
        }
      }
    } catch (error) {
      console.error('Failed to load story:', error);
      
      // Check if upgrade is required
      if (error.response?.status === 403 && error.response?.data?.requiresUpgrade) {
        setUpgradeMessage(error.response.data.message || 'Please upgrade to read all stories.');
        setShowUpgradeModal(true);
        setLoading(false);
        return;
      }
      
      toast.error(error.response?.data?.message || 'Failed to load story');
      navigate('/stories');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchStory();
  }, [fetchStory]);

  const handleToggleFavorite = async () => {
    try {
      // Use encoded ID for API call (backend will decode it)
      const response = await api.patch(`/stories/${id}/favorite`);
      if (response.data.success) {
        setStory((prev) => ({ ...prev, is_favorite: response.data.isFavorite }));
        toast.success(response.data.isFavorite ? 'Added to favorites' : 'Removed from favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  // Speech synthesis setup
  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      setSpeechSupported(false);
      return;
    }
    setSpeechSupported(true);

    const loadVoices = () => {
      const v = window.speechSynthesis.getVoices();
      if (v.length > 0) {
        setVoices(v);
      }
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      window.speechSynthesis.onvoiceschanged = null;
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    };
  }, []);

  // Map language names to language codes for speech synthesis
  const getLanguageCode = (language) => {
    if (!language) return 'en-US';
    const lang = language.toLowerCase();
    const languageMap = {
      'english': 'en-US',
      'spanish': 'es-ES',
      'french': 'fr-FR',
      'german': 'de-DE',
      'italian': 'it-IT',
      'portuguese': 'pt-PT',
      'dutch': 'nl-NL',
      'swedish': 'sv-SE',
      'norwegian': 'no-NO',
      'danish': 'da-DK',
      'finnish': 'fi-FI',
      'polish': 'pl-PL',
      'russian': 'ru-RU',
      'japanese': 'ja-JP',
      'korean': 'ko-KR',
      'chinese': 'zh-CN',
      'hindi': 'hi-IN',
      'bengali': 'bn-BD',
      'arabic': 'ar-SA',
      'turkish': 'tr-TR',
      'greek': 'el-GR',
      'hebrew': 'he-IL',
      'thai': 'th-TH',
      'vietnamese': 'vi-VN',
      'indonesian': 'id-ID',
      'malay': 'ms-MY',
      'czech': 'cs-CZ',
      'hungarian': 'hu-HU',
      'romanian': 'ro-RO',
      'ukrainian': 'uk-UA',
    };
    return languageMap[lang] || 'en-US';
  };

  const pickVoice = (preference = 'male', languageCode = 'en-US') => {
    if (!voices || voices.length === 0) return null;
    
    // First, try to find a voice that matches both language and gender preference
    const target = preference.toLowerCase() === 'female' ? ['female', 'woman'] : ['male', 'man'];
    
    // Try to find voice matching language first
    const langMatched = voices.find((v) => {
      const langMatch = v.lang && v.lang.toLowerCase().startsWith(languageCode.toLowerCase().split('-')[0]);
      const genderMatch = target.some((hint) => 
        v.name.toLowerCase().includes(hint) || (v.voiceURI || '').toLowerCase().includes(hint)
      );
      return langMatch && genderMatch;
    });
    
    if (langMatched) return langMatched;
    
    // If no language+gender match, try just language
    const langOnly = voices.find((v) => 
      v.lang && v.lang.toLowerCase().startsWith(languageCode.toLowerCase().split('-')[0])
    );
    
    if (langOnly) return langOnly;
    
    // Fallback to gender preference
    const genderMatched = voices.find((v) =>
      target.some((hint) => v.name.toLowerCase().includes(hint) || (v.voiceURI || '').toLowerCase().includes(hint))
    );
    
    return genderMatched || voices[0];
  };

  const handleListen = () => {
    if (!speechSupported) {
      toast.error('Story voice is not supported in this browser.');
      return;
    }
    if (!story || !story.content) return;

    // If already speaking, stop
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    // Get language from story profile
    const storyLanguage = story.profile_language || story.language || 'english';
    const languageCode = getLanguageCode(storyLanguage);
    
    const preferredVoice = story.voice || story.profile_voice || 'male';
    const voiceToUse = pickVoice(preferredVoice, languageCode);

    const utterance = new SpeechSynthesisUtterance(story.content);
    
    // Set language for proper pronunciation
    utterance.lang = languageCode;
    
    if (voiceToUse) {
      utterance.voice = voiceToUse;
    }
    utterance.rate = 1;
    utterance.pitch = 1;

    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    utteranceRef.current = utterance;
    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  const handleShare = async () => {
    if (!story) return;

    const storyUrl = `${window.location.origin}/stories/${id}`;
    const shareData = {
      title: story.title,
      text: `Check out this amazing story: ${story.title}`,
      url: storyUrl,
    };

    // Try Web Share API first (works on mobile devices and modern browsers)
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        toast.success('Story shared successfully!');
      } catch (error) {
        // User cancelled or error occurred
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          // Fall back to copy link
          await copyToClipboard(storyUrl);
        }
      }
    } else {
      // Fallback: Copy link to clipboard
      await copyToClipboard(storyUrl);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success('Story link copied to clipboard!');
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Story link copied to clipboard!');
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy link. Please try again.');
    }
  };

  const handleDownloadPDF = async () => {
    if (!story) return;

    try {
      toast.loading('Preparing PDF download...', { id: 'pdf-download' });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;
      let yPosition = margin;

      // Add cover image if available
      if (story.thumbnail) {
        try {
          // Load image and convert to base64
          const response = await fetch(story.thumbnail);
          if (!response.ok) throw new Error('Failed to fetch image');
          const blob = await response.blob();
          const reader = new FileReader();
          
          const imageData = await new Promise((resolve, reject) => {
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          // Create image element to get dimensions
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = imageData;
          });

          // Calculate image dimensions to fit page width while maintaining aspect ratio
          const imgWidth = contentWidth;
          const imgHeight = (img.height * imgWidth) / img.width;
          const maxImageHeight = pageHeight * 0.4; // Max 40% of page height

          let finalImgWidth = imgWidth;
          let finalImgHeight = imgHeight;

          if (imgHeight > maxImageHeight) {
            finalImgHeight = maxImageHeight;
            finalImgWidth = (img.width * finalImgHeight) / img.height;
          }

          // Center image horizontally
          const imgX = (pageWidth - finalImgWidth) / 2;

          pdf.addImage(imageData, 'JPEG', imgX, yPosition, finalImgWidth, finalImgHeight);
          yPosition += finalImgHeight + 15;
        } catch (imgError) {
          console.error('Error loading image for PDF:', imgError);
          // Continue without image if it fails to load - PDF will still be generated with text content
        }
      }

      // Create a temporary container for story content to capture with html2canvas
      // This ensures proper rendering of all languages (Bengali, Hindi, Arabic, etc.)
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.width = `${contentWidth}mm`;
      tempContainer.style.padding = '20px';
      tempContainer.style.fontFamily = 'system-ui, -apple-system, sans-serif';
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.color = '#000000';
      tempContainer.style.fontSize = '16px';
      tempContainer.style.lineHeight = '1.6';
      
      // Add title
      const titleDiv = document.createElement('div');
      titleDiv.style.fontSize = '28px';
      titleDiv.style.fontWeight = 'bold';
      titleDiv.style.marginBottom = '15px';
      titleDiv.style.lineHeight = '1.4';
      titleDiv.style.wordWrap = 'break-word';
      titleDiv.textContent = story.title;
      tempContainer.appendChild(titleDiv);

      // Add metadata
      const metadataDiv = document.createElement('div');
      metadataDiv.style.fontSize = '12px';
      metadataDiv.style.color = '#666666';
      metadataDiv.style.marginBottom = '20px';
      metadataDiv.textContent = `Starring: ${story.character_name || 'N/A'} | ${story.duration || 5} min read | ${new Date(story.created_at).toLocaleDateString()}`;
      tempContainer.appendChild(metadataDiv);

      // Add story content
      const contentDiv = document.createElement('div');
      contentDiv.style.fontSize = '14px';
      contentDiv.style.lineHeight = '1.8';
      contentDiv.style.whiteSpace = 'pre-wrap';
      contentDiv.style.wordWrap = 'break-word';
      contentDiv.textContent = story.content;
      tempContainer.appendChild(contentDiv);

      document.body.appendChild(tempContainer);

      try {
        // Capture the content as image using html2canvas (preserves all language rendering)
        const canvas = await html2canvas(tempContainer, {
          scale: 2, // Higher quality
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
        });

        // Calculate dimensions to fit PDF page
        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const imgData = canvas.toDataURL('image/png');

        // Check if content fits on one page, otherwise split into multiple pages
        const availableHeight = pageHeight - yPosition - margin;
        
        if (imgHeight <= availableHeight) {
          // Fits on one page
          pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
        } else {
          // Need to split into multiple pages
          const totalHeight = imgHeight;
          const pageContentHeight = pageHeight - 2 * margin;
          let sourceY = 0;
          let remainingHeight = totalHeight;

          while (remainingHeight > 0) {
            if (yPosition > margin) {
              pdf.addPage();
              yPosition = margin;
            }

            const heightToAdd = Math.min(remainingHeight, pageContentHeight);
            const heightRatio = heightToAdd / totalHeight;
            const sourceHeight = canvas.height * heightRatio;

            // Create a temporary canvas for this page slice
            const pageCanvas = document.createElement('canvas');
            pageCanvas.width = canvas.width;
            pageCanvas.height = sourceHeight;
            const pageCtx = pageCanvas.getContext('2d');
            pageCtx.drawImage(
              canvas,
              0, sourceY, canvas.width, sourceHeight,
              0, 0, canvas.width, sourceHeight
            );

            const pageImgData = pageCanvas.toDataURL('image/png');
            pdf.addImage(pageImgData, 'PNG', margin, yPosition, imgWidth, heightToAdd);

            sourceY += sourceHeight;
            remainingHeight -= heightToAdd;
            yPosition += heightToAdd;
          }
        }
      } catch (canvasError) {
        console.error('Error capturing content with html2canvas:', canvasError);
        // Fallback: Use text rendering (may not support all languages properly)
        pdf.setFontSize(20);
        pdf.setFont('helvetica', 'bold');
        const titleLines = pdf.splitTextToSize(story.title, contentWidth);
        pdf.text(titleLines, margin, yPosition);
        yPosition += titleLines.length * 8 + 10;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const metadataText = `Starring: ${story.character_name || 'N/A'} | ${story.duration || 5} min read | ${new Date(story.created_at).toLocaleDateString()}`;
        pdf.setTextColor(100, 100, 100);
        pdf.text(metadataText, margin, yPosition);
        yPosition += 10;
        pdf.setTextColor(0, 0, 0);

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        const contentLines = pdf.splitTextToSize(story.content, contentWidth);
        
        contentLines.forEach((line) => {
          if (yPosition > pageHeight - margin) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin, yPosition);
          yPosition += 6;
        });
      } finally {
        // Clean up temporary container
        document.body.removeChild(tempContainer);
      }

      // Generate filename (preserve Unicode characters in filename)
      const filename = `${story.title.replace(/[<>:"/\\|?*]/g, '_').trim() || 'story'}.pdf`;

      // Save the PDF
      pdf.save(filename);
      
      toast.success('PDF downloaded successfully!', { id: 'pdf-download' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.', { id: 'pdf-download' });
    }
  };

  useEffect(() => {
    // Pre-fill user's name if available
    if (user) {
      setReviewForm(prev => {
        // Only update if displayName is empty
        if (!prev.displayName) {
          return {
            ...prev,
            displayName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email?.split('@')[0] || '',
          };
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleReviewInput = (field, value) => {
    setReviewForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.displayName.trim() || reviewForm.content.trim().length < 20) {
      toast.error('Please provide your name and at least 20 characters in your review.');
      return;
    }
    try {
      setSubmittingReview(true);
      await api.post('/reviews', {
        displayName: reviewForm.displayName.trim(),
        relationship: null,
        location: null,
        childName: story?.character_name || null,
        childAge: null,
        rating: Number(reviewForm.rating),
        headline: reviewForm.headline.trim() || `Review for: ${story?.title || 'Story'}`,
        content: reviewForm.content.trim(),
        source: `story_${id}`,
        userId: user?.id || null,
      });
      toast.success('Thanks for your review! We\'ll review it shortly.');
      setReviewForm({
        displayName: reviewForm.displayName.trim(), // Keep name for potential next review
        rating: 5,
        headline: '',
        content: '',
      });
      setShowReviewForm(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to submit review right now.');
    } finally {
      setSubmittingReview(false);
    }
  };

  const renderStars = (rating, interactive = false, onRatingChange = null) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((idx) => (
        <button
          key={idx}
          type={interactive ? 'button' : 'button'}
          onClick={interactive && onRatingChange ? () => onRatingChange(idx) : undefined}
          className={interactive ? 'cursor-pointer focus:outline-none' : 'cursor-default'}
          disabled={!interactive}
        >
          <Star
            className={`h-5 w-5 transition-colors ${
              idx <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
            } ${interactive ? 'hover:scale-110' : ''}`}
          />
        </button>
      ))}
    </div>
  );

  if (loading) {
    return null;
  }

  if (!story) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <p className="text-gray-600 mb-4">Story not found.</p>
            <button
              onClick={() => navigate('/stories')}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold"
            >
              Back to Stories
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 flex flex-col">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-4xl flex-1">
        <button
          onClick={() => navigate('/stories')}
          className="flex items-center text-gray-600 hover:text-purple-600 mb-6 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Stories
        </button>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Story Thumbnail */}
          {story.thumbnail && (
            <div className="mb-6">
              <img
                src={story.thumbnail}
                alt={story.title}
                className="w-full h-64 md:h-80 lg:h-96 object-cover rounded-xl shadow-lg"
                onError={(e) => {
                  // Hide image if it fails to load
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
          
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">{story.title}</h1>
              <div className="flex items-center gap-4 text-gray-600">
                <span className="text-purple-600 font-semibold">Starring {story.character_name}</span>
                {story.theme && (
                  <span className="px-3 py-1 bg-purple-50 text-purple-600 rounded-full text-sm">
                    {story.theme}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={handleToggleFavorite}
              className="p-3 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Heart
                className={`h-6 w-6 ${
                  story.is_favorite ? 'fill-red-500 text-red-500' : 'text-gray-400'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center gap-4 mb-8 text-sm text-gray-500">
            <span className="flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              {story.duration || 5} min read
            </span>
            <span>{new Date(story.created_at).toLocaleDateString()}</span>
          </div>

          <div className="prose max-w-none mb-8">
            <div className="text-gray-700 leading-relaxed text-lg whitespace-pre-wrap">
              {story.content}
            </div>
          </div>

          <div className="flex gap-4 pt-6 border-t border-gray-200">
            <button
              onClick={handleListen}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all font-semibold"
            >
              {isSpeaking ? (
                <>
                  <Pause className="h-5 w-5 mr-2 inline" />
                  Stop
                </>
              ) : (
                <>
                  <Play className="h-5 w-5 mr-2 inline" />
                  Listen to Story
                </>
              )}
            </button>
            <button
              onClick={handleShare}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
              title="Share story"
            >
              <Share2 className="h-5 w-5" />
            </button>
            <button
              onClick={handleDownloadPDF}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
              title="Download PDF"
            >
              <Download className="h-5 w-5" />
            </button>
          </div>

          {/* Review Section */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            {!showReviewForm ? (
              <div className="text-center">
                <MessageSquare className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">What did you think of this story?</h3>
                <p className="text-gray-600 mb-6">Share your thoughts and help other families discover great stories!</p>
                <button
                  onClick={() => setShowReviewForm(true)}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all font-semibold"
                >
                  Write a Review
                </button>
              </div>
            ) : (
              <div className="max-w-2xl mx-auto">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Write a Review</h3>
                <form onSubmit={handleSubmitReview} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={reviewForm.displayName}
                      onChange={(e) => handleReviewInput('displayName', e.target.value)}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                      placeholder="Enter your name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rating <span className="text-red-500">*</span>
                    </label>
                    {renderStars(reviewForm.rating, true, (rating) => handleReviewInput('rating', rating))}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Headline (Optional)
                    </label>
                    <input
                      type="text"
                      value={reviewForm.headline}
                      onChange={(e) => handleReviewInput('headline', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                      placeholder="e.g., 'Amazing story my child loved!'"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Review <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={reviewForm.content}
                      onChange={(e) => handleReviewInput('content', e.target.value)}
                      required
                      rows={6}
                      minLength={20}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none resize-none"
                      placeholder="Tell us what you and your child thought about this story... (minimum 20 characters)"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {reviewForm.content.length} characters (minimum 20)
                    </p>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="submit"
                      disabled={submittingReview || reviewForm.content.trim().length < 20}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowReviewForm(false);
                        setReviewForm({
                          displayName: reviewForm.displayName,
                          rating: 5,
                          headline: '',
                          content: '',
                        });
                      }}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          navigate('/stories');
        }}
        message={upgradeMessage}
      />
    </div>
  );
};

export default StoryDetail;

